import { Add, CleaningServices } from '@mui/icons-material';
import { Button, Checkbox, FormControlLabel, FormGroup, Grid, Paper, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import swal from 'sweetalert';
import { Encabezado } from '../../Components/Encabezado/Encabezado';
import AreaOrganizacionalNuevo from '../../Components/Modals/AreaOrganizacional/AreaOrganizacionalNuevo';
import Loading from '../../Components/Modals/Loading/Loader';
import { PermissionData } from '../../context/Component/PermissionData';
import Table from './../../Components/Table/Table';

import { FormProvider, useForm } from 'react-hook-form';
import {
  fetchAreaOrganizacionalEliminar,
  fetchAreasOrganizacionales,
  fetchAreasOrganizacionalesPagination,
  fetchAreasOrganizacionalesPaginationCount,
  fetchCambiarSuccess,
  selectAllAreasOrganizacionales,
  selectAreaOrganizacionalesPagination,
  selectAreaOrganizacionalesPaginationCount,
  selectIsLoading,
  selectIsSuccess,
  selectMessage,
} from '../../redux/AreaOrganizacional/AreaOrganizacionalSlice';
import { fetchCargosAll } from '../../redux/Cargos/CargosSlice';

const filterDefaultValues = {
  areaId: 0,
  activo: true,
  nombre: '',
};

const openDefaultValue = { open: false, area: undefined };

export const AreaOrganizacionales = () => {
  // * PERMISOS DEL USUARIO.
  const permisosState = PermissionData();

  // ? HOOK-SELECTOR
  const isLoading = useSelector(selectIsLoading);
  const areasOrganizacionales = useSelector(selectAreaOrganizacionalesPagination);
  const areasOrganizacionalesCount = useSelector(selectAreaOrganizacionalesPaginationCount);
  const areaOrganizacionalesSelect = useSelector(selectAllAreasOrganizacionales);

  const dispatch = useDispatch();
  const isSuccess = useSelector(selectIsSuccess);
  const message = useSelector(selectMessage);

  // ? HOOK-STATE
  const [open, setOpen] = useState(openDefaultValue);
  const areasOrganizacionalesSelectState = useMemo(() => {
    return [{ id: 0, nombre: 'Seleccionar Área' }, ...areaOrganizacionalesSelect];
  }, [areaOrganizacionalesSelect]);

  // ? HOOK-STATE DE PAGINACION.
  const [filtro, setFiltro] = useState(filterDefaultValues);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [skipRow, setSkipRow] = useState(0);
  const [refreshData, setRefreshData] = useState(true);

  const form = useForm({
    defaultValues: {
      area: {
        id: 0,
        nombre: '',
        encargadoId: null,
        padre: null,
        areaMaxima: null,
        lft: 0,
        rgt: 0,
        lvl: 0,
        rootId: 0,
      },
    },
  });

  // * FUNCION PARA REFRESCAR LISTA
  const refreshList = useCallback(
    ({ filtroValue = filtro, skipValue = skipRow, rowsValue = rowsPerPage } = {}) => {
      const payload = {
        ...filtroValue,
        index: skipValue,
        itemsPerPage: rowsValue,
      };

      dispatch(fetchAreasOrganizacionalesPagination(payload));
      dispatch(fetchAreasOrganizacionalesPaginationCount(payload));
    },
    [dispatch, filtro, skipRow, rowsPerPage]
  );

  // * FUNCION PARA REFRESCAR SUCCESS
  const refreshSuccess = useCallback(() => {
    dispatch(fetchCambiarSuccess());
  }, [dispatch]);

  const deleteAreaOrganizacional = (area) => {
    swal({
      title: `Esta seguro de eliminar el área organizacional "${area?.nombre.replaceAll('-', '')}"?`,
      text: 'Una vez eliminado, no estara nuevamente disponible!',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        dispatch(fetchAreaOrganizacionalEliminar(area?.id));
      }
    });
  };

  useEffect(() => {
    dispatch(fetchAreasOrganizacionales());
    dispatch(fetchCargosAll());
  }, [dispatch]);

  useEffect(() => {
    if (refreshData) {
      refreshList();
      setRefreshData(false);
    }
  }, [skipRow, rowsPerPage, refreshData, refreshList]);

  useEffect(() => {
    if (!message || message.length === 0) {
      return;
    }

    if (isSuccess === 'success') {
      swal('Exitoso!', message, 'success');
      setOpen(openDefaultValue);
      setFiltro(filterDefaultValues);
      refreshSuccess();
      form.reset();
      setRefreshData(true);
    }

    if (isSuccess === 'error') {
      swal('Error', message, 'error');
      refreshSuccess();
    }
  }, [isSuccess, message, refreshSuccess, form]);

  // * FUNCION PARA LA PAGINACION
  const handleChangePage = (event, newPage) => {
    setSkipRow(newPage * rowsPerPage);
    setPage(newPage);
    setRefreshData(true);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSkipRow(0);
    setRefreshData(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setRefreshData(true);
    }
  };

  const saveFiltro = ({ name, data }) => {
    setFiltro({ ...filtro, [name]: data });
    setRefreshData(true);
  };

  return (
    <Encabezado title="Áreas Organizacionales">
      <Paper elevation={2} sx={{ p: 2, m: 2 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={3}>
            <Typography fontSize={13} fontWeight="bold" mb={0.5}>
              Nombre
            </Typography>

            <TextField
              name="nombre"
              fullWidth
              size="medium"
              value={filtro.nombre}
              onKeyDown={handleKeyDown}
              onChange={({ target }) => setFiltro({ ...filtro, nombre: target.value })}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography fontSize={13} fontWeight="bold" mb={0.5}>
              Área
            </Typography>

            <Select
              classNamePrefix="select"
              isSearchable
              placeholder="Seleccionar Área Organizacional"
              options={areaOrganizacionalesSelect}
              getOptionLabel={(option) => option.nombre}
              getOptionValue={(option) => option.id}
              onChange={(e) => saveFiltro({ name: 'areaId', data: e.id })}
              value={areasOrganizacionalesSelectState.find((x) => x.id === filtro.areaId)}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormGroup sx={{ mt: { xs: 0, md: 3 } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="activo"
                    checked={!filtro.activo}
                    onChange={({ target }) => saveFiltro({ name: target.name, data: !target.checked })}
                  />
                }
                label="Área Inactivas"
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              id="btn-clean-empleados"
              variant="contained"
              color="error"
              type="button"
              title="Limpiar filtros"
              sx={{ mt: { xs: 0, md: 3 }, minWidth: 40 }}
              onClick={() => {
                setFiltro(filterDefaultValues);
                setRefreshData(true);
              }}
            >
              <CleaningServices />
            </Button>
          </Grid>

          <Grid item xs={12} md={2} textAlign={{ xs: 'left', md: 'right' }}>
            <button
              type="button"
              hidden={!permisosState.ROLE_CREAR_AREAS_ORGANIZACIONALES}
              className="btn btn-primary-mem mt-3 "
              onClick={() => setOpen({ open: true, area: undefined })}
            >
              Agregar <Add />
            </button>
          </Grid>
        </Grid>
      </Paper>

      {!isLoading && areasOrganizacionales && (
        <div className="row mt-3 m-2">
          <Table
            setOpenEdit={setOpen}
            deleteAreaOrganizacional={deleteAreaOrganizacional}
            rowsColumn={areasOrganizacionales}
            areasOrganizacionalesCount={areasOrganizacionalesCount}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
          />
        </div>
      )}

      <FormProvider {...form}>
        <AreaOrganizacionalNuevo open={open} setOpen={setOpen} openDefaultValue={openDefaultValue} />
      </FormProvider>

      <Loading open={isLoading} />
    </Encabezado>
  );
};

export default AreaOrganizacionales;
